(() => {
  const SELECTOR = '[data-product-estimated-delivery]';
  const CACHE_KEY = 'bubble_me_delivery_pincode';
  const metroPrefixes = ['110', '121', '122', '201', '400', '401', '411', '421', '500', '560', '600', '700', '380'];
  const remotePrefixes = ['175', '176', '177', '182', '184', '185', '190', '191', '192', '193', '194', '737', '744', '790', '791', '792', '793', '794', '795', '796', '797', '798', '799', '900'];
  const jsonCache = new Map();

  // Offline pincode -> place-name maps, ported verbatim from the previous theme's
  // product-estimated-delivery.js (PIN_LABEL_BY_PREFIX and STATE_BY_TWO_DIGIT).
  //
  // The rewrite dropped both and resolved the place name over the network
  // instead, which left the widget showing "Delivering to pincode 110001"
  // instead of "Delivering to Delhi" whenever the network path failed:
  //   - api.postalpincode.in sends no Access-Control-Allow-Origin, so a browser
  //     fetch is blocked by CORS and can never succeed
  //   - the templates pointed at `edd-special-pincodes.json`, which 404s. The
  //     real file is `bubbleme-edd.json` (673 same-day pincodes, ACAO: *), now
  //     configured in all four product templates. It only covers the 7 metros
  //     it serves same-day, so every other pincode still needs these maps.
  // These maps need no network and cannot fail.
  const CITY_BY_THREE_DIGIT = {
'110':'Delhi','121':'Faridabad','122':'Gurugram','201':'Noida / Ghaziabad','400':'Mumbai','401':'Mumbai','411':'Pune','421':'Thane','560':'Bengaluru','500':'Hyderabad','600':'Chennai','700':'Kolkata','380':'Ahmedabad','302':'Jaipur','134':'Panchkula','160':'Chandigarh','226':'Lucknow','452':'Indore','462':'Bhopal','682':'Kochi','440':'Nagpur','395':'Surat','390':'Vadodara','530':'Visakhapatnam','641':'Coimbatore','800':'Patna','208':'Kanpur','141':'Ludhiana','282':'Agra','422':'Nashik','492':'Raipur','248':'Dehradun','751':'Bhubaneswar','781':'Guwahati','570':'Mysuru','342':'Jodhpur','313':'Udaipur','190':'Srinagar','194':'Leh / Ladakh','744':'Andaman & Nicobar','737':'Sikkim','790':'Arunachal Pradesh','791':'Arunachal Pradesh','793':'Meghalaya','795':'Manipur','796':'Mizoram','797':'Nagaland','799':'Tripura'
  };

  const STATE_BY_TWO_DIGIT = {
'11':'Delhi','12':'Haryana','13':'Haryana','14':'Punjab','15':'Punjab','16':'Chandigarh','17':'Himachal Pradesh','18':'Jammu & Kashmir','19':'Jammu & Kashmir / Ladakh','20':'Uttar Pradesh','21':'Uttar Pradesh','22':'Uttar Pradesh','23':'Uttar Pradesh','24':'Uttar Pradesh / Uttarakhand','25':'Uttar Pradesh','26':'Uttar Pradesh / Uttarakhand','27':'Uttar Pradesh','28':'Uttar Pradesh','30':'Rajasthan','31':'Rajasthan','32':'Rajasthan','33':'Rajasthan','34':'Rajasthan','36':'Gujarat','37':'Gujarat','38':'Gujarat','39':'Gujarat','40':'Maharashtra / Goa','41':'Maharashtra','42':'Maharashtra','43':'Maharashtra','44':'Maharashtra','45':'Madhya Pradesh','46':'Madhya Pradesh','47':'Madhya Pradesh','48':'Madhya Pradesh','49':'Chhattisgarh','50':'Telangana','51':'Andhra Pradesh','52':'Andhra Pradesh','53':'Andhra Pradesh','56':'Karnataka','57':'Karnataka','58':'Karnataka','59':'Karnataka','60':'Tamil Nadu','61':'Tamil Nadu','62':'Tamil Nadu','63':'Tamil Nadu','64':'Tamil Nadu','67':'Kerala','68':'Kerala','69':'Kerala','70':'West Bengal','71':'West Bengal','72':'West Bengal','73':'West Bengal / Sikkim','74':'West Bengal / Andaman','75':'Odisha','76':'Odisha','77':'Odisha','78':'Assam','79':'North East','80':'Bihar / Jharkhand','81':'Bihar / Jharkhand','82':'Bihar / Jharkhand','83':'Bihar / Jharkhand','84':'Bihar','85':'Bihar','90':'Army Postal Service'
  };

  /**
   * Resolves a human place name for an Indian pincode without a network call.
   *
   * @param {string} pin - Six-digit pincode.
   * @returns {string} City, else state, else an empty string.
   */
  const localityFromPin = (pin) =>
    CITY_BY_THREE_DIGIT[pin.slice(0, 3)] || STATE_BY_TWO_DIGIT[pin.slice(0, 2)] || '';


  const cleanPin = (value) => String(value || '').replace(/\D/g, '').slice(0, 6);
  const validPin = (pin) => /^[1-9]\d{5}$/.test(pin);

  const fetchWithTimeout = async (url, timeout = 5200) => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeout);
    try {
      return await fetch(url, { headers: { Accept: 'application/json' }, signal: controller.signal });
    } finally {
      window.clearTimeout(timer);
    }
  };

  const loadDeliveryJson = async (url) => {
    if (!url) return null;
    if (!jsonCache.has(url)) {
      jsonCache.set(url, fetchWithTimeout(url)
        .then((response) => response.ok ? response.json() : null)
        .catch(() => null));
    }
    return jsonCache.get(url);
  };

  const configuredRule = async (root, pin) => {
    const json = await loadDeliveryJson(root.dataset.jsonUrl);
    if (!json || typeof json !== 'object') return null;
    const compactPin = json.p?.[pin];
    if (Array.isArray(compactPin)) {
      const delivery = json.delivery || {};
      const istHour = Number(new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        hour12: false
      }).format(new Date()));
      const isBeforeCutoff = istHour < Number(delivery.cutoffHour ?? 15);
      const promise = isBeforeCutoff ? delivery.beforeCutoff : delivery.afterCutoff;
      return {
        city: json.cities?.[compactPin[0]] || compactPin[0],
        area: compactPin[1],
        range: promise?.range || delivery.range,
        message: promise?.note || delivery.note,
        darkStore: compactPin[2]
      };
    }
    const rule = json.pincodes?.[pin] || json[pin];
    if (!rule || typeof rule !== 'object') return null;
    const merged = { ...(json.defaults || {}), ...rule };
    if (merged.cutoff && (merged.beforeCutoff || merged.afterCutoff)) {
      const match = String(merged.cutoff).match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?/i);
      if (match) {
        let cutoffHour = Number(match[1]);
        if (match[3]?.toUpperCase() === 'PM' && cutoffHour < 12) cutoffHour += 12;
        if (match[3]?.toUpperCase() === 'AM' && cutoffHour === 12) cutoffHour = 0;
        const istHour = Number(new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Asia/Kolkata',
          hour: '2-digit',
          hour12: false
        }).format(new Date()));
        Object.assign(merged, istHour < cutoffHour ? merged.beforeCutoff : merged.afterCutoff);
      }
    }
    return merged;
  };

  const postalLocation = async (pin) => {
    try {
      const response = await fetchWithTimeout(`https://api.postalpincode.in/pincode/${encodeURIComponent(pin)}`);
      if (!response.ok) return null;
      const data = await response.json();
      const office = data?.[0]?.PostOffice?.[0];
      if (!office) return null;
      return [office.District || office.Name, office.State].filter(Boolean).join(', ');
    } catch {
      return null;
    }
  };

  const defaultRange = (root, pin) => {
    const prefix = pin.slice(0, 3);
    if (remotePrefixes.includes(prefix) || pin.startsWith('9')) return root.dataset.remoteRange;
    if (metroPrefixes.includes(prefix)) return root.dataset.metroRange;
    if (/^[1-8]/.test(pin)) return root.dataset.normalRange;
    return root.dataset.fallbackRange;
  };

  const render = (root, { error = false, title, message, note = '' }) => {
    const result = root.querySelector('[data-delivery-result]');
    root.classList.toggle('has-error', error);
    result.hidden = false;
    root.querySelector('[data-delivery-title]').textContent = title;
    root.querySelector('[data-delivery-message]').textContent = message;
    root.querySelector('[data-delivery-note]').textContent = note;
  };

  const check = async (root) => {
    if (root.dataset.deliveryBusy === 'true') return;
    const input = root.querySelector('[data-delivery-pincode]');
    const button = root.querySelector('[data-delivery-check]');
    const pin = cleanPin(input.value);
    input.value = pin;

    if (!validPin(pin)) {
      render(root, { error: true, title: 'Invalid pincode', message: 'Please enter a valid 6-digit Indian pincode.' });
      return;
    }

    root.dataset.deliveryBusy = 'true';
    button.disabled = true;
    button.querySelector('span').textContent = 'Checking';

    try {
      const rule = await configuredRule(root, pin);
      const location = rule?.area || rule?.city || localityFromPin(pin) || (await postalLocation(pin));
      const range = rule?.range || rule?.label || rule?.title || defaultRange(root, pin);
      render(root, {
        title: rule?.displayTitle || `Delivering to ${location || `pincode ${pin}`}`,
        message: `Estimated delivery: ${range}`,
        note: [
          rule?.message || (!rule?.darkStore ? 'Timeline may vary slightly based on courier service and order timing.' : ''),
          rule?.darkStore ? `Fulfilled from ${rule.darkStore}.` : ''
        ].filter(Boolean).join(' ')
      });
      try { localStorage.setItem(CACHE_KEY, pin); } catch {}
    } catch {
      render(root, {
        title: `Delivering to pincode ${pin}`,
        message: `Estimated delivery: ${defaultRange(root, pin)}`,
        note: 'Showing an estimate based on your PIN zone.'
      });
    } finally {
      root.dataset.deliveryBusy = 'false';
      button.disabled = false;
      button.querySelector('span').textContent = 'Check';
    }
  };

  const init = (root) => {
    if (root.dataset.deliveryInitialized === 'true') return;
    root.dataset.deliveryInitialized = 'true';
    const input = root.querySelector('[data-delivery-pincode]');
    const customerPin = cleanPin(root.dataset.customerPin);
    let savedPin = '';
    try { savedPin = cleanPin(localStorage.getItem(CACHE_KEY)); } catch {}
    input.value = validPin(customerPin) ? customerPin : (validPin(savedPin) ? savedPin : '');
    input.addEventListener('input', () => { input.value = cleanPin(input.value); });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        check(root);
      }
    });
    root.querySelector('[data-delivery-check]').addEventListener('click', () => check(root));
  };

  const initAll = (scope = document) => scope.querySelectorAll(SELECTOR).forEach(init);
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', () => initAll(), { once: true })
    : initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
